import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Eye, EyeOff, KeyRound, Lock } from "lucide-react";
import { teacherApi } from "../../features/teacher/teacherApi";
import {
  TeacherAuthField,
  TeacherAuthLayout,
  TeacherFeedbackMessage,
} from "./TeacherUI";
import { getTeacherAuthErrorMessage } from "../../utils/uiErrorMessages";

export default function TeacherResetPassword() {
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: "",
    confirm_password: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [generalMessage, setGeneralMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const token = useMemo(() => String(searchParams.get("token") || "").trim(), [searchParams]);

  const validationQuery = useQuery({
    queryKey: ["teacher-reset-token", token],
    queryFn: () => teacherApi.validateResetPasswordToken(token),
    enabled: Boolean(token),
    retry: false,
  });

  const resetMutation = useMutation({
    mutationFn: teacherApi.submitResetPassword,
    onSuccess: () => {
      setSuccessMessage("Password reset completed successfully. You can log in now.");
      setGeneralMessage("");
      setFieldErrors({});
      setFormData({
        password: "",
        confirm_password: "",
      });
    },
    onError: (error) => {
      setFieldErrors(error.fieldErrors || {});
      setGeneralMessage(getTeacherAuthErrorMessage(error, "reset-password"));
      setSuccessMessage("");
    },
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    setFieldErrors({});
    setGeneralMessage("");
    setSuccessMessage("");

    const nextErrors = {};

    if (!token) {
      setGeneralMessage("Missing reset token in the query string.");
      return;
    }

    if (!formData.password) {
      nextErrors.password = "Password is required.";
    }

    if (!formData.confirm_password) {
      nextErrors.confirm_password = "Please confirm the password.";
    }

    if (
      formData.password &&
      formData.confirm_password &&
      formData.password !== formData.confirm_password
    ) {
      nextErrors.confirm_password = "Passwords do not match.";
    }

    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors);
      return;
    }

    resetMutation.mutate({
      token,
      password: formData.password,
      confirm_password: formData.confirm_password,
    });
  };

  const tokenInvalid =
    !token ||
    Boolean(validationQuery.error) ||
    validationQuery.data?.valid === false ||
    validationQuery.data?.is_valid === false;

  return (
    <TeacherAuthLayout
      title="Reset Password"
      subtitle="Validate the token, then submit the new teacher password."
      footer={
        <p className="teacher-auth-footer-text">
          Want to return? <Link className="teacher-auth-link" to="/teacherlogin">Back to login</Link>
        </p>
      }
    >
      {!token ? (
        <TeacherFeedbackMessage variant="error">
          Missing token. Open this page using the reset link from the backend email.
        </TeacherFeedbackMessage>
      ) : null}
      {validationQuery.isLoading ? (
        <TeacherFeedbackMessage variant="info">Validating reset token...</TeacherFeedbackMessage>
      ) : null}
      {token && validationQuery.error ? (
        <TeacherFeedbackMessage variant="error">
          {getTeacherAuthErrorMessage(validationQuery.error, "reset-password-token")}
        </TeacherFeedbackMessage>
      ) : null}
      {generalMessage ? (
        <TeacherFeedbackMessage variant="error">{generalMessage}</TeacherFeedbackMessage>
      ) : null}
      {successMessage ? (
        <TeacherFeedbackMessage variant="success">{successMessage}</TeacherFeedbackMessage>
      ) : null}

      <form className="teacher-auth-form" onSubmit={handleSubmit} noValidate>
        <TeacherAuthField
          id="teacher-reset-token"
          label="Token"
          icon={KeyRound}
          name="token"
          type="text"
          value={token}
          readOnly
          note="This value is read from the query string."
        />

        <TeacherAuthField
          id="teacher-reset-password"
          label="New Password"
          icon={Lock}
          name="password"
          type={showPassword ? "text" : "password"}
          value={formData.password}
          onChange={(event) =>
            setFormData((previous) => ({
              ...previous,
              password: event.target.value,
            }))
          }
          placeholder="Enter your new password"
          error={fieldErrors.password}
          trailingAction={
            <button
              className="teacher-auth-toggle"
              type="button"
              onClick={() => setShowPassword((previous) => !previous)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          }
        />

        <TeacherAuthField
          id="teacher-reset-confirm-password"
          label="Confirm Password"
          icon={Lock}
          name="confirm_password"
          type={showConfirmPassword ? "text" : "password"}
          value={formData.confirm_password}
          onChange={(event) =>
            setFormData((previous) => ({
              ...previous,
              confirm_password: event.target.value,
            }))
          }
          placeholder="Re-enter the new password"
          error={fieldErrors.confirm_password}
          trailingAction={
            <button
              className="teacher-auth-toggle"
              type="button"
              onClick={() => setShowConfirmPassword((previous) => !previous)}
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          }
        />

        <button
          className="teacher-auth-button"
          type="submit"
          disabled={resetMutation.isPending || tokenInvalid || validationQuery.isLoading}
        >
          {resetMutation.isPending ? "Resetting..." : "Reset password"}
        </button>
      </form>
    </TeacherAuthLayout>
  );
}
